import { DataTable } from "@primereact/ui/datatable";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ALL_PERMISSIONS,
  SUPPORTED_LOCALES,
  type Locale,
  type Permission,
  type RoleResponse,
  type UserResponse,
} from "@offertli/shared";
import { StatusBadge } from "../../components/StatusBadge.js";
import { Button } from "../../components/ui/Button.js";
import { Checkbox } from "@primereact/ui/checkbox";
import { Field } from "../../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { PageHeader } from "../../components/ui/PageHeader.js";
import { Select } from "@primereact/ui/select";
import { Textarea } from "@primereact/ui/textarea";
import {
  apiDelete,
  apiGet,
  apiGetPage,
  apiPatch,
  apiPost,
} from "../../lib/api.js";
import { useAppConfig } from "../../lib/app-config.js";
import { useMe } from "../../lib/auth.js";
import {
  ConfirmButton,
  ErrorPanel,
  LoadingPanel,
  MutationError,
  Section,
} from "../shared.js";
type RoleDraft = {
  id?: string;
  name: string;
  description: string;
  permissions: Permission[];
  isSystem?: boolean;
};
const SYSTEM_ROLE_KEYS = {
  Administrator: "administrator",
  Employee: "employee",
  Office: "office",
} as const;
function systemRoleKey(role: { name: string; isSystem?: boolean }) {
  if (role.isSystem === false) return null;
  return SYSTEM_ROLE_KEYS[role.name as keyof typeof SYSTEM_ROLE_KEYS] ?? null;
}
export function UsersPage() {
  const { t } = useTranslation();
  const appConfig = useAppConfig();
  const queryClient = useQueryClient();
  const me = useMe();
  const [showInvite, setShowInvite] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [locale, setLocale] = useState<Locale>(appConfig.defaultLocale);
  const localeChanged = useRef(false);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  useEffect(() => {
    if (!localeChanged.current) setLocale(appConfig.defaultLocale);
  }, [appConfig.defaultLocale]);
  const users = useQuery({
    queryKey: ["users"],
    queryFn: () =>
      apiGetPage<UserResponse>("/users", {
        pageSize: 100,
      }),
  });
  const roles = useQuery({
    queryKey: ["roles"],
    queryFn: () =>
      apiGet<{
        data: RoleResponse[];
      }>("/roles").then((response) => response.data),
  });
  const invite = useMutation({
    mutationFn: () =>
      apiPost("/users", {
        firstName,
        lastName,
        email,
        locale,
        roleIds,
      }),
    onSuccess: async () => {
      setShowInvite(false);
      setFirstName("");
      setLastName("");
      setEmail("");
      setRoleIds([]);
      await queryClient.invalidateQueries({
        queryKey: ["users"],
      });
    },
  });
  const userAction = useMutation({
    mutationFn: ({ id, action }: { id: string; action: string }) =>
      apiPost(`/users/${id}/${action}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["users"],
      });
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    invite.mutate();
  };
  return (
    <>
      <PageHeader
        title={t("users.title")}
        subtitle={t("users.subtitle")}
        actions={
          <Button
            onClick={() => {
              setShowInvite((value) => !value);
              localeChanged.current = false;
              setLocale(appConfig.defaultLocale);
            }}
          >
            {t("users.invite")}
          </Button>
        }
      />
      {showInvite ? (
        <Section title={t("users.newInvitation")} className="mb-5">
          <form className="grid gap-4 sm:grid-cols-2" onSubmit={submit}>
            <Field label={t("common.firstName")} required>
              <Input
                required
                value={firstName}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setFirstName(event.target.value)
                }
              />
            </Field>
            <Field label={t("common.lastName")} required>
              <Input
                required
                value={lastName}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setLastName(event.target.value)
                }
              />
            </Field>
            <Field label={t("common.email")} required>
              <Input
                type="email"
                required
                value={email}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setEmail(event.target.value)
                }
              />
            </Field>
            <Field label={t("common.language")}>
              <Select.Root
                value={locale}
                onValueChange={(event: { value: unknown }) => {
                  localeChanged.current = true;
                  setLocale(event.value as Locale);
                }}
                fluid
                options={[
                  ...SUPPORTED_LOCALES.map((supportedLocale) => ({
                    value: supportedLocale,
                    label: t(`locale.${supportedLocale}`),
                  })),
                ]}
                optionLabel="label"
                optionValue="value"
              >
                <Select.Trigger type="button">
                  <Select.Value />
                  <Select.Indicator className="pi pi-chevron-down" />
                </Select.Trigger>
                <Select.Portal>
                  <Select.Positioner>
                    <Select.Popup>
                      <Select.List />
                    </Select.Popup>
                  </Select.Positioner>
                </Select.Portal>
              </Select.Root>
            </Field>
            <fieldset className="sm:col-span-2">
              <legend className="mb-2 text-sm font-medium">
                {t("users.roles")}
              </legend>
              <div className="flex flex-wrap gap-3">
                {roles.data?.map((role) => (
                  <label
                    key={role.id}
                    className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
                  >
                    <Checkbox.Root
                      inputId={`invite-role-${role.id}`}
                      checked={roleIds.includes(role.id)}
                      onCheckedChange={(event: { checked: boolean }) =>
                        setRoleIds(
                          event.checked
                            ? [...roleIds, role.id]
                            : roleIds.filter((id) => id !== role.id),
                        )
                      }
                    >
                      <Checkbox.Box>
                        <Checkbox.Indicator match="checked">
                          <i className="pi pi-check" />
                        </Checkbox.Indicator>
                      </Checkbox.Box>
                    </Checkbox.Root>
                    {systemRoleKey(role)
                      ? t(`roles.system.${systemRoleKey(role)}.name`)
                      : role.name}
                  </label>
                ))}
              </div>
            </fieldset>
            <MutationError error={invite.error} />
            <div className="sm:col-span-2 flex gap-2">
              <Button
                type="submit"
                disabled={roleIds.length === 0}
                loading={invite.isPending || undefined}
              >
                {t("users.sendInvite")}
              </Button>
              <Button
                type="button"
                variant="outlined"
                severity="secondary"
                onClick={() => setShowInvite(false)}
              >
                {t("common.cancel")}
              </Button>
            </div>
          </form>
        </Section>
      ) : null}
      {users.isLoading ? (
        <LoadingPanel />
      ) : users.error ? (
        <ErrorPanel error={users.error} />
      ) : (
        <DataTable.Root>
          <DataTable.TableContainer>
            <DataTable.Table>
              <DataTable.THead>
                <DataTable.Row>
                  <DataTable.THeadCell>
                    <DataTable.THeadTitle>
                      {t("users.name")}
                    </DataTable.THeadTitle>
                  </DataTable.THeadCell>
                  <DataTable.THeadCell>
                    <DataTable.THeadTitle>
                      {t("common.email")}
                    </DataTable.THeadTitle>
                  </DataTable.THeadCell>
                  <DataTable.THeadCell>
                    <DataTable.THeadTitle>
                      {t("users.roles")}
                    </DataTable.THeadTitle>
                  </DataTable.THeadCell>
                  <DataTable.THeadCell>
                    <DataTable.THeadTitle>
                      {t("common.status")}
                    </DataTable.THeadTitle>
                  </DataTable.THeadCell>
                  <DataTable.THeadCell>
                    <DataTable.THeadTitle>
                      {t("users.actions")}
                    </DataTable.THeadTitle>
                  </DataTable.THeadCell>
                </DataTable.Row>
              </DataTable.THead>
              <DataTable.TBody className="divide-y divide-app-border">
                {users.data?.data.map((user) => (
                  <DataTable.Row key={user.id}>
                    <DataTable.Cell className="font-medium">
                      <Link
                        className="text-brand hover:underline"
                        to={`/employees/${user.id}`}
                      >
                        {user.firstName} {user.lastName}
                      </Link>
                    </DataTable.Cell>
                    <DataTable.Cell>{user.email}</DataTable.Cell>
                    <DataTable.Cell>
                      {user.roles
                        .map((role) =>
                          systemRoleKey(role)
                            ? t(`roles.system.${systemRoleKey(role)}.name`)
                            : role.name,
                        )
                        .join(", ")}
                    </DataTable.Cell>
                    <DataTable.Cell>
                      <StatusBadge status={user.status} />
                    </DataTable.Cell>
                    <DataTable.Cell>
                      <div className="flex gap-2">
                        {user.status === "invited" ? (
                          <Button
                            size="small"
                            variant="outlined"
                            severity="secondary"
                            onClick={() =>
                              userAction.mutate({
                                id: user.id,
                                action: "resend-invitation",
                              })
                            }
                          >
                            {t("users.resend")}
                          </Button>
                        ) : null}
                        {["active", "suspended"].includes(user.status) &&
                        Boolean(me.data?.user.id) &&
                        user.id !== me.data?.user.id ? (
                          <ConfirmButton
                            confirmMessage={t("users.deactivateConfirm")}
                            onConfirm={() =>
                              userAction.mutate({
                                id: user.id,
                                action: "deactivate",
                              })
                            }
                          >
                            {t("users.deactivate")}
                          </ConfirmButton>
                        ) : null}
                      </div>
                    </DataTable.Cell>
                  </DataTable.Row>
                ))}
                {!users.data?.data.length ? (
                  <DataTable.Row>
                    <DataTable.Cell
                      colSpan={5}
                      className="py-10 text-center text-app-muted"
                    >
                      {t("users.empty")}
                    </DataTable.Cell>
                  </DataTable.Row>
                ) : null}
              </DataTable.TBody>
            </DataTable.Table>
          </DataTable.TableContainer>
        </DataTable.Root>
      )}
      <div className="mt-6">
        <RolesPanel roles={roles.data ?? []} loading={roles.isLoading} />
      </div>
    </>
  );
}
export function RolesPanel({
  roles,
  loading,
}: {
  roles: RoleResponse[];
  loading: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const permissions = useQuery({
    queryKey: ["permissions"],
    queryFn: () =>
      apiGet<{
        groups: Record<string, string[]>;
        data: Permission[];
      }>("/permissions"),
  });
  const [draft, setDraft] = useState<RoleDraft | null>(null);
  const save = useMutation({
    mutationFn: () => {
      if (!draft) return Promise.resolve();
      const body = {
        name: draft.name,
        description: draft.description || undefined,
        permissions: draft.permissions,
      };
      return draft.id
        ? apiPatch(`/roles/${draft.id}`, body)
        : apiPost("/roles", body);
    },
    onSuccess: async () => {
      setDraft(null);
      await queryClient.invalidateQueries({
        queryKey: ["roles"],
      });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => apiDelete(`/roles/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["roles"],
      });
    },
  });
  return (
    <Section title={t("roles.title")}>
      {loading ? (
        <LoadingPanel />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {roles.map((role) => (
            <button
              type="button"
              key={role.id}
              onClick={() =>
                setDraft({
                  id: role.id,
                  name: role.name,
                  description: role.description ?? "",
                  permissions: role.isAdmin
                    ? [...ALL_PERMISSIONS]
                    : [...role.permissions],
                  isSystem: role.isSystem,
                })
              }
              className="rounded-lg border border-app-border p-4 text-left hover:bg-[var(--p-content-hover-background)]"
            >
              <strong>
                {systemRoleKey(role)
                  ? t(`roles.system.${systemRoleKey(role)}.name`)
                  : role.name}
              </strong>
              <span className="mt-1 block text-sm text-app-muted">
                {systemRoleKey(role)
                  ? t(`roles.system.${systemRoleKey(role)}.description`)
                  : role.description ||
                    t("roles.permissionCount", {
                      count: role.permissions.length,
                    })}
              </span>
              <span className="mt-2 block text-xs text-app-muted">
                {t("roles.userCount", {
                  count: role.userCount,
                })}
              </span>
            </button>
          ))}
          <button
            type="button"
            onClick={() =>
              setDraft({
                name: "",
                description: "",
                permissions: [],
              })
            }
            className="rounded-lg border border-dashed border-app-border p-4 text-left font-medium text-brand"
          >
            + {t("roles.add")}
          </button>
        </div>
      )}
      {draft ? (
        <form
          className="mt-5 space-y-4 border-t border-app-border pt-5"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("roles.name")} required>
              <Input
                required
                disabled={draft.isSystem}
                value={
                  systemRoleKey(draft)
                    ? t(`roles.system.${systemRoleKey(draft)}.name`)
                    : draft.name
                }
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setDraft({
                    ...draft,
                    name: event.target.value,
                  })
                }
              />
            </Field>
            <Field label={t("roles.description")}>
              <Textarea
                disabled={draft.isSystem}
                rows={2}
                value={
                  systemRoleKey(draft)
                    ? t(`roles.system.${systemRoleKey(draft)}.description`)
                    : draft.description
                }
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setDraft({
                    ...draft,
                    description: event.target.value,
                  })
                }
              />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {permissions.data
              ? Object.entries(permissions.data.groups).map(
                  ([group, actions]) => (
                    <fieldset
                      key={group}
                      className="rounded-lg border border-app-border p-3"
                    >
                      <legend className="px-1 text-sm font-semibold">
                        {t(`roles.permissionGroups.${group}`, {
                          defaultValue:
                            group.charAt(0).toUpperCase() + group.slice(1),
                        })}
                      </legend>
                      {actions.map((action) => {
                        const permission = `${group}.${action}` as Permission;
                        return (
                          <label
                            key={permission}
                            className="mt-2 flex items-start gap-2 text-sm"
                          >
                            <Checkbox.Root
                              inputId={`role-permission-${permission}`}
                              disabled={draft.isSystem}
                              checked={draft.permissions.includes(permission)}
                              onCheckedChange={(event: { checked: boolean }) =>
                                setDraft((current) =>
                                  current
                                    ? {
                                        ...current,
                                        permissions: event.checked
                                          ? [
                                              ...current.permissions.filter(
                                                (value) => value !== permission,
                                              ),
                                              permission,
                                            ]
                                          : current.permissions.filter(
                                              (value) => value !== permission,
                                            ),
                                      }
                                    : current,
                                )
                              }
                            >
                              <Checkbox.Box>
                                <Checkbox.Indicator match="checked">
                                  <i className="pi pi-check" />
                                </Checkbox.Indicator>
                              </Checkbox.Box>
                            </Checkbox.Root>
                            <span>
                              {t(
                                `roles.permissionActions.${action.replaceAll(".", "_")}`,
                                {
                                  defaultValue: action
                                    .replaceAll("_", " ")
                                    .replaceAll(".", " "),
                                },
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </fieldset>
                  ),
                )
              : null}
          </div>
          <MutationError error={save.error || remove.error} />
          <div className="flex gap-2">
            <Button
              type="submit"
              disabled={draft.isSystem}
              loading={save.isPending || undefined}
            >
              {t("common.save")}
            </Button>
            <Button
              type="button"
              variant="outlined"
              severity="secondary"
              onClick={() => setDraft(null)}
            >
              {t("common.cancel")}
            </Button>
            {draft.id && !draft.isSystem ? (
              <ConfirmButton
                confirmMessage={t("roles.deleteConfirm")}
                onConfirm={() => remove.mutate(draft.id!)}
              >
                {t("common.delete")}
              </ConfirmButton>
            ) : null}
          </div>
        </form>
      ) : null}
    </Section>
  );
}
